import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext } from 'react';
import { MedialinkDialogHeaderTagsWithoutPillGroup } from '../../../../../../pages/material/components/MedialinkDialogHeaderTags';
import { ExerciseResult } from '../../@types/exercise-types';
import { useExerciseManagerContext } from '../../contexts/ExerciseManagerContext';
import {
  ExerciseTranslationsContext,
  ExerciseTranslationsContextValue,
} from '../../contexts/ExerciseTranslationsContext';
import { useMaterialLinkBuilder } from '../../../../../../hooks/useMaterialLinkBuilder';
import { getExerciseModuleResult, getScoreAsPercentage } from '../../util/score-utils';
import ExerciseHighLightedItem from '../exercise-highlighted-item';
import type { Medialink } from '../../../../../../api/medialink';
import ModuleContext from '../../../../../../pages/material/_contexts/ModuleContext';

type CompletedSessionMessages = ExerciseTranslationsContextValue['progressionModalCompletedSession']['messages'];
type ExerciseModuleMessages = NonNullable<
  ExerciseTranslationsContextValue['progressionModalCompletedSession']['exerciseModule']
>;

interface InsufficientFeedbackProps {
  medialink: Medialink;
  messages: ExerciseModuleMessages['insufficient'];
}

function InsufficientFeedback({ medialink, messages }: InsufficientFeedbackProps) {
  const { buildMaterialTocLink } = useMaterialLinkBuilder();
  const { allModules } = useContext(ModuleContext);

  const moduleSlug = allModules.find(m => m.id === medialink.moduleId)?.slug;
  const nodeId = medialink.hierarchy[medialink.hierarchy.length - 1].id;

  const materialLink = buildMaterialTocLink(nodeId, moduleSlug);

  return (
    <ol>
      <li>
        {messages.step1}
        {materialLink ? (
          <a href={materialLink} target="_blank" rel="noopener noreferrer">
            {messages.step1LinkText}
          </a>
        ) : (
          messages.step1LinkText
        )}
        .
      </li>
      <li>{messages.step2}</li>
    </ol>
  );
}

interface ExerciseModuleFeedbackProps {
  percentage: number;
  medialink: Medialink;
  messages: ExerciseModuleMessages;
}

function ExerciseModuleFeedback({ percentage, medialink, messages }: ExerciseModuleFeedbackProps) {
  const result = getExerciseModuleResult(percentage);
  const msg = messages[result];

  return (
    <>
      <h3>{msg.title}</h3>
      <p>{msg.p1}</p>
      {result === 'insufficient' && <InsufficientFeedback medialink={medialink} messages={messages.insufficient} />}
      {'stepsHtml' in msg && <div dangerouslySetInnerHTML={{ __html: msg.stepsHtml }} />}
      {'p2' in msg && <p>{msg.p2}</p>}
    </>
  );
}

interface ShowScoreMessageProps {
  percentage: number;
  unachievable: boolean;
  messages: CompletedSessionMessages['showScore'];
}

function ShowScoreMessage({ percentage, unachievable, messages }: ShowScoreMessageProps) {
  if (unachievable) {
    return (
      <>
        <p>{messages.adaptive.p1}</p>
        <p>{messages.adaptive.p2}</p>
      </>
    );
  }

  const { p1, p2 } = percentage === 100 ? messages.perfectResult : messages.result;

  return (
    <>
      <p>{p1}</p>
      <p>{p2}</p>
    </>
  );
}

interface DefaultMessageProps {
  percentage: number;
  unachievable: boolean;
  messages: Pick<CompletedSessionMessages, 'adaptive' | 'veryGood' | 'good' | 'bad'>;
}

function DefaultMessage({ percentage, unachievable, messages }: DefaultMessageProps) {
  if (unachievable) return <p>{messages.adaptive}</p>;
  if (percentage > 66) return <p>{messages.veryGood}</p>;
  if (percentage > 33) return <p>{messages.good}</p>;
  return <p>{messages.bad}</p>;
}

interface Props {
  result: ExerciseResult;
  onReview: (sessionId: string) => void;
  onRestart?: () => void;
}

function ProgressionModalCompletedSession({ result, onReview, onRestart }: Props) {
  const translations = useContext(ExerciseTranslationsContext);

  const { showScore, medialink, module, onClose } = useExerciseManagerContext();

  // result for a completed session always has a score, so we can safely assert that it is not undefined
  const percentage = getScoreAsPercentage(result.score!.achieved, result.score!.achievable);
  const { session } = result;

  function renderMessage() {
    if (module.isExerciseModule) {
      return (
        <ExerciseModuleFeedback
          percentage={percentage}
          medialink={medialink}
          messages={translations.progressionModalCompletedSession.exerciseModule}
        />
      );
    }

    if (showScore) {
      return (
        <ShowScoreMessage
          percentage={percentage}
          unachievable={!!medialink.unachievable}
          messages={translations.progressionModalCompletedSession.messages.showScore}
        />
      );
    }

    return (
      <DefaultMessage
        percentage={percentage}
        unachievable={!!medialink.unachievable}
        messages={translations.progressionModalCompletedSession.messages}
      />
    );
  }

  return (
    <Dialog
      id="progression-modal-completed-session"
      modal
      onClose={onClose}
      aria-label={translations.progressionModalCompletedSession.a11yLabel}
    >
      <Dialog.Header
        title={translations.progressionModalCompletedSession.title}
        onClose={onClose}
        aria-label={translations.closeDialog}
      />
      <Dialog.Body>
        <div className="modal-content-text">{renderMessage()}</div>
        <ExerciseHighLightedItem
          status="finished"
          medialink={medialink}
          label={translations.progressionModalCompletedSession.highlightedItem.label}
          scoreTitle={translations.progressionModalCompletedSession.highlightedItem.score}
          percentageTitle={translations.progressionModalCompletedSession.highlightedItem.percentage}
          resultTitle={translations.progressionModalCompletedSession.highlightedItem.resultTitle}
          score={result.score}
          tags={{
            'aria-label': undefined,
            role: undefined,
            children: <MedialinkDialogHeaderTagsWithoutPillGroup module={module} material={medialink} />,
          }}
          phases={result.phases}
          isLastPhase={result.isLastPhase}
        />
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsLeft={
          onRestart
            ? {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="neutral" onClick={onRestart}>
                    {translations.progressionModalCompletedSession.buttons.restart}
                  </Button>
                ),
              }
            : undefined
        }
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              {session && (
                <Button onClick={() => onReview(session)} prominence="neutral" key="neutral">
                  {translations.progressionModalCompletedSession.buttons.review}
                </Button>
              )}
              <Button onClick={onClose} prominence="success" key="success">
                {translations.progressionModalCompletedSession.buttons.close}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}

export default ProgressionModalCompletedSession;
